import { ContractReporter, IContractDrift } from './contract.types';

/**
 * Drift reporting: dedupe, sample, and hand off to whatever sink the app installed.
 *
 * A schema mismatch is a property of the *deployment*, not of the request — if the API
 * dropped a field, every single response carries the same drift. Reporting each one would
 * bury the signal and, on a busy support session, cost more than the bug. So a given
 * signature is reported once per session.
 */

let reporter: ContractReporter | undefined;

/** Signatures already reported this session. */
const seen = new Set<string>();

/** Bound the set so a pathological mismatch storm cannot grow it without limit. */
const MAX_TRACKED_SIGNATURES = 200;

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Install the sink that receives drift reports. Call once during app startup, before
 * `configureCore`. Apps typically forward to Sentry:
 *
 * ```ts
 * setContractReporter(drift => Sentry.captureMessage(`API contract drift: ${drift.contract}`, {
 *     level: 'warning',
 *     extra: { ...drift },
 * }));
 * ```
 *
 * With no reporter installed the layer still logs in development and stays silent in
 * production, so forgetting to wire it degrades to "no telemetry", never to a crash.
 */
export function setContractReporter(next: ContractReporter | undefined): void {
    reporter = next;
}

/** Test seam — clears the dedupe set so cases do not leak into each other. */
export function resetContractReporter(): void {
    reporter = undefined;
    seen.clear();
}

/**
 * A drift's identity is the contract plus the shape of what disagreed — not the values.
 * Two responses missing the same field are one problem, however many rows differ.
 */
function getSignature(drift: IContractDrift): string {
    const issueKeys = drift.issues
        // Strip array indices: `[0].coverage` and `[7].coverage` are the same defect.
        .map(issue => `${issue.path.replace(/\[\d+\]/g, '[]')}:${issue.code}`)
        .sort();

    return `${drift.contract}|${Array.from(new Set(issueKeys)).join(',')}`;
}

export function reportContractDrift(drift: IContractDrift): void {
    const signature = getSignature(drift);

    if (seen.has(signature)) {
        return;
    }

    if (seen.size < MAX_TRACKED_SIGNATURES) {
        seen.add(signature);
    }

    if (isDevelopment) {
        /* tslint:disable-next-line:no-console */
        console.warn(
            `[contract] ${drift.contract} (${drift.path}) returned ${drift.issueCount} unexpected field(s):`,
            drift.issues,
        );
    }

    try {
        reporter?.(drift);
    } catch {
        // A broken telemetry sink must never take down the request it was observing.
    }
}
